import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicSplitData } from '../types';

interface SigningSuccessComponentProps {
  signData: PublicSplitData;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const SigningSuccessComponent: React.FC<SigningSuccessComponentProps> = ({ signData }) => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#18181B] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-[#27272A] rounded-2xl shadow-xl p-8 border border-[#3F3F46]">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[#10B98133] rounded-full flex items-center justify-center mb-4 border border-[#10B98130]">
            <svg className="w-8 h-8 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            🎉 Split Confirmed Successfully!
          </h1>
          <p className="text-[#9CA3AF]">
            Thank you for confirming your share of "{signData.splitTitle}"
          </p>
        </div>

        {/* Signing Details */}
        <div className="bg-[#14B8A610] rounded-2xl p-6 border border-[#3F3F46] mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-[#9CA3AF] mb-2">Confirmed By</h3>
              <p className="text-white">{signData.signedName}</p>
            </div>
            {signData.signedAt && (
              <div>
                <h3 className="font-semibold text-[#9CA3AF] mb-2">Confirmed On</h3>
                <p className="text-white">{formatDate(signData.signedAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sign Up CTA - only show if user doesn't already exist */}
        {!signData.userAlreadyExists && (
          <div className="bg-[#14B8A620] rounded-2xl p-6 border border-[#14B8A640] mb-6">
            <h3 className="font-semibold text-[#14B8A6] mb-3">Want to manage your own splits?</h3>
            <p className="text-[#9CA3AF] mb-4">
              Create a free CoTune account to track all your splits, collaborate with others, and keep your music ownership organized.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-6 py-3 bg-[#14B8A6] hover:bg-[#0D9488] text-white font-medium rounded-xl transition-colors"
            >
              Create Free Account
            </button>
          </div>
        )}

        {/* What happens next */}
        <div className="bg-[#60A5FA10] rounded-2xl p-6 border border-[#60A5FA30] mb-6">
          <h3 className="font-semibold text-[#60A5FA] mb-3">What happens next?</h3>
          <div className="space-y-2 text-[#93C5FD]">
            <p>• Once all collaborators have confirmed, you'll receive a confirmation email with the final record</p>
            <p>• The split creator will be notified of your confirmation</p>
            <p>• You can bookmark this page to check the status anytime</p>
          </div>
        </div>

        {/* Split Information */}
        <div className="bg-[#3F3F46] rounded-2xl p-6 border border-[#3F3F46]">
          <h3 className="font-semibold text-[#9CA3AF] mb-4">Split Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-[#9CA3AF]">Split Title:</span>
              <p className="text-white font-medium">{signData.splitTitle}</p>
            </div>
            {signData.createdAt && (
              <div>
                <span className="text-[#9CA3AF]">Created:</span>
                <p className="text-white">{formatDate(signData.createdAt)}</p>
              </div>
            )}
            <div>
              <span className="text-[#9CA3AF]">Created By:</span>
              <p className="text-white">{signData.ownerName}</p>
            </div>
            <div>
              <span className="text-[#9CA3AF]">Your Role:</span>
              <p className="text-white capitalize">{signData.collaborator.role}</p>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="text-center mt-8 pt-6 border-t border-[#3F3F46]">
          <p className="text-[#9CA3AF] text-sm mb-2">
            Questions about this split?
          </p>
          <p className="text-[#9CA3AF] text-sm">
            Contact support at{' '}
            <a href="mailto:support@cotunemusic.com" className="text-[#14B8A6] hover:text-[#0D9488]">
              support@cotunemusic.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SigningSuccessComponent;
